import styled from '@emotion/styled';
import {
  Avatar,
  IconButton,
  ListItem as _ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core';
import CloudDownload from '@material-ui/icons/CloudDownload';
import Delete from '@material-ui/icons/Delete';
import FontDownload from '@material-ui/icons/FontDownload';
import Image from '@material-ui/icons/Image';
import InsertDriveFile from '@material-ui/icons/InsertDriveFile';
import MoreVert from '@material-ui/icons/MoreVert';
import Videocam from '@material-ui/icons/Videocam';
import { createElement, FC, Fragment, MouseEvent, MouseEventHandler, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import mq from '../../../../styleUtility/mediaquery';
import { FileReference } from '../../../../types/Files';
import FileSize from '../../../primitive/FileSize';

export type Props = {
  file: FileReference;
  isVisibleSecondary?: boolean;
  onClickDeleteButton?: MouseEventHandler<HTMLElement>;
  className?: string;
  isPC?: boolean;
};

const FileListItem: FC<Props> = ({ file, isVisibleSecondary, onClickDeleteButton, className, isPC }) => {
  const menuButtonRef = useRef<HTMLElement>();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const renderIcon = () => {
    if (file.mimetype.startsWith('image/')) {
      return <Image />;
    } else if (file.mimetype.startsWith('video/')) {
      return <Videocam />;
    } else if (file.mimetype.startsWith('font/')) {
      return <FontDownload />;
    } else {
      return <InsertDriveFile />;
    }
  };

  const OriginalName = onClickDeleteButton ? OriginalNameWithButton : OriginalNameWithoutButton;

  const onClickMenuButton = () => setIsMenuOpen(true);
  const onRequestCloseMenu = () => setIsMenuOpen(false);
  const handleClickDeleteButton = (e: MouseEvent<HTMLElement>) => {
    onRequestCloseMenu();
    if (onClickDeleteButton) {
      onClickDeleteButton(e);
    }
  };

  return (
    <>
      <ListItem button className={className}>
        <ListItemIcon>
          <Avatar>{renderIcon()}</Avatar>
        </ListItemIcon>
        <ListItemText
          primary={
            <OriginalName variant="subtitle1" noWrap>
              {file.originalName}
            </OriginalName>
          }
          secondary={
            isVisibleSecondary != null && isVisibleSecondary ? (
              <TypographyText variant="body2" color="textSecondary" noWrap>
                <FileSize fileSize={file.size}>
                  {isPC
                    ? (fileSize) => `${fileSize} - ${file.createdAt.toFormat('yyyy-MM-dd HH:mm:ss')}`
                    : (fileSize) => `${fileSize}\n${file.createdAt.toFormat('yyyy-MM-dd HH:mm:ss')}`}
                </FileSize>
              </TypographyText>
            ) : null
          }
          disableTypography
        />
        <ListItemSecondaryAction>
          <IconButton component="a" href={file.url} target="_blank">
            <CloudDownload />
          </IconButton>

          {onClickDeleteButton ? (
            <IconButton onClick={onClickMenuButton} buttonRef={menuButtonRef}>
              <MoreVert />
            </IconButton>
          ) : null}
        </ListItemSecondaryAction>
      </ListItem>

      {onClickDeleteButton ? (
        <Menu anchorEl={menuButtonRef.current} open={isMenuOpen} onClose={onRequestCloseMenu}>
          <MenuItem onClick={handleClickDeleteButton}>
            <ListItemIcon>
              <Delete />
            </ListItemIcon>

            <FormattedMessage id="file_list.file_list_item.delete">
              {(label: string) => <ListItemText inset primary={label} />}
            </FormattedMessage>
          </MenuItem>
        </Menu>
      ) : null}
    </>
  );
};

const ListItem = styled(_ListItem)`
  ${mq('sp')} {
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const OriginalNameWithoutButton = styled(Typography)`
  padding-right: 0;
`;

const OriginalNameWithButton = styled(Typography)`
  padding-right: 48px;
`;

const TypographyText = styled(Typography)`
  ${mq('sp')} {
    width: 90px;
    white-space: pre;
  }
`;

export default FileListItem;
